import React from 'react';

import {
  CurrencyExchange as CurrencyExchangeIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { Box, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList } from '@mui/material';

import {
  useStripeRefundVerificationRequestChargeMutation,
  useVerificationRequestPaymentInfoQuery,
  VerificationRequestDocument,
  VerificationRequestPaymentInfoDocument,
  VerificationRequestPaymentInfoQueryVariables,
  VerificationRequestQueryVariables,
  VerificationRequestStripePaymentStatus,
} from 'src/api';
import { Popover } from 'src/components/common';
import { ModalId } from 'src/constants/modals';
import { useNotification } from 'src/hooks/app';
import { useModalV2 } from 'src/hooks/shared';
import { useVerificationRequest } from 'src/hooks/verificationRequest';
import { EntityActionConfirmationModal } from 'src/modals/common';

type InboxItemPlatformStaffMenuProps = {
  inboxItemId: string;
};

export const InboxItemPlatformStaffMenu: React.FC<InboxItemPlatformStaffMenuProps> = ({
  inboxItemId,
}) => {
  const menuButtonAriaId = React.useId();

  const { openModal } = useModalV2();
  const { createApolloContext } = useNotification();

  const { verificationRequest, loading } = useVerificationRequest({ inboxItemId });
  const { data: verificationRequestPaymentInfo } = useVerificationRequestPaymentInfoQuery({
    variables: {
      where: { inboxItemId },
    },
  });

  const [refundCharge] = useStripeRefundVerificationRequestChargeMutation({
    refetchQueries: [
      {
        query: VerificationRequestDocument,
        variables: { filter: { inboxItemId } } satisfies VerificationRequestQueryVariables,
      },
      {
        query: VerificationRequestPaymentInfoDocument,
        variables: {
          where: { inboxItemId },
        } satisfies VerificationRequestPaymentInfoQueryVariables,
      },
    ],
    context: createApolloContext({
      successMessage: 'The refund has been processed',
      errorMessage: 'Failed to process the refund',
    }),
  });

  const handleRefundPayment = React.useCallback(() => {
    openModal(EntityActionConfirmationModal, {
      id: ModalId.EntityActionConfirmation,
      onSubmit: async () => {
        await refundCharge({ variables: { inboxItemId } });
      },
      action: 'refund',
      entity: 'transaction',
      actionButtonColor: 'error',
    });
  }, [openModal, refundCharge, inboxItemId]);

  return (
    <Box>
      <Popover
        target={
          <IconButton
            id={menuButtonAriaId}
            aria-label="menu"
            aria-haspopup="true"
            loading={loading}
          >
            <MoreIcon />
          </IconButton>
        }
        // eslint-disable-next-line react/no-unstable-nested-components
        content={() => (
          <MenuList aria-labelledby={menuButtonAriaId}>
            <MenuItem
              onClick={handleRefundPayment}
              disabled={
                !verificationRequest ||
                verificationRequestPaymentInfo?.verificationRequest?.stripePaymentStatus !==
                  VerificationRequestStripePaymentStatus.Succeeded
              }
            >
              <ListItemIcon>
                <CurrencyExchangeIcon />
              </ListItemIcon>
              <ListItemText>Refund payment</ListItemText>
            </MenuItem>
          </MenuList>
        )}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </Box>
  );
};
